import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI } from '../services/api';
import { cartStorage } from '../utils/cart';
import { Copy, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [pixInfo, setPixInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/carrinho');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && !order) {
      createOrder();
    }
  }, [isAuthenticated]);

  const createOrder = async () => {
    try {
      setLoading(true);
      const cart = cartStorage.getCart();
      
      if (cart.length === 0) {
        navigate('/carrinho');
        return;
      }

      const items = cart.map(item => ({
        product_id: item.productId,
        product_name: item.productName,
        plan_id: item.planId,
        plan_type: item.planType,
        price: item.price,
        validity: item.validity
      }));

      const response = await ordersAPI.create(items);
      setOrder(response.order);
      setPixInfo(response.pix);
      cartStorage.clearCart();
    } catch (error) {
      console.error('Order creation error:', error);
      toast({
        title: '❌ Erro ao criar pedido',
        description: 'Tente novamente mais tarde',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (pixInfo?.pix_code) {
      navigator.clipboard.writeText(pixInfo.pix_code);
      setCopied(true);
      toast({
        title: '✅ Código copiado!',
        description: 'Cole no seu app de pagamento'
      });
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white gaming-title mb-2">PROCESSANDO...</h1>
          <p className="text-gray-400">Gerando seu pedido</p>
        </div>
      </div>
    );
  }

  if (!order || !pixInfo) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 gaming-title glow-red">
            PEDIDO CRIADO!
          </h1>
          <p className="text-xl text-gray-400">
            Realize o pagamento via PIX para receber seu acesso
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardHeader className="border-b border-red-900/30">
              <CardTitle className="text-white gaming-title">RESUMO DO PEDIDO</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <p className="text-gray-400 text-sm">Pedido ID</p>
                <p className="text-white font-bold gaming-title">{order.order_id}</p>
              </div>
              
              {order.items.map((item, idx) => (
                <div key={idx} className="border-t border-red-900/20 pt-4">
                  <p className="text-white font-semibold">{item.product_name}</p>
                  <p className="text-gray-400 text-sm">Plano: {item.plan_type}</p>
                  <p className="text-red-600 font-bold">R$ {item.price.toFixed(2)}</p>
                </div>
              ))}
              
              <div className="border-t border-red-900/20 pt-4">
                <div className="flex justify-between text-lg">
                  <span className="text-white font-bold gaming-title">TOTAL</span>
                  <span className="text-red-600 font-bold gaming-title text-2xl">
                    R$ {order.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* PIX Payment */}
          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardHeader className="border-b border-red-900/30">
              <CardTitle className="text-white gaming-title">PAGAMENTO VIA PIX</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div>
                <p className="text-gray-400 text-sm mb-2">Destinatário</p>
                <p className="text-white font-semibold">{pixInfo.recipient_name}</p>
                <p className="text-gray-400 text-sm">{pixInfo.pix_key}</p>
              </div>

              {/* QR Code Image */}
              {pixInfo.qr_image && (
                <div className="mb-6">
                  <p className="text-gray-400 text-sm mb-3 text-center font-semibold">📱 Escaneie o QR Code</p>
                  <div className="bg-white rounded-xl p-8 flex items-center justify-center shadow-inner">
                    <img 
                      src={pixInfo.qr_image} 
                      alt="QR Code PIX" 
                      className="w-full max-w-sm h-auto"
                      style={{ 
                        imageRendering: '-webkit-optimize-contrast',
                        imageRendering: 'crisp-edges',
                        imageRendering: 'pixelated',
                        maxWidth: '400px',
                        width: '100%',
                        height: 'auto'
                      }}
                    />
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-2">
                    💡 Para melhor leitura, aproxime a câmera do QR Code
                  </p>
                </div>
              )}

              <div>
                <p className="text-gray-400 text-sm mb-3">Ou use Código PIX Copia e Cola</p>
                <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20 mb-3">
                  <p className="text-gray-300 text-sm break-all font-mono">
                    {pixInfo.pix_code}
                  </p>
                </div>
                <Button
                  onClick={copyPixCode}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {copied ? 'COPIADO!' : 'COPIAR CÓDIGO PIX'}
                </Button>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20">
                <p className="text-gray-400 text-sm font-semibold mb-2">📱 Instruções:</p>
                <ol className="text-gray-400 text-xs space-y-1">
                  {pixInfo.instructions?.map((instruction, idx) => (
                    <li key={idx}>{instruction}</li>
                  ))}
                </ol>
              </div>

              <p className="text-center text-xs text-gray-500">
                ⚡ Após o pagamento, você receberá seu acesso por email
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/pedidos')}
            variant="outline"
            className="border-red-600 text-white hover:bg-red-600/20"
          >
            Ver Meus Pedidos
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;